import { ADMIN_ROLES } from '@avanta/shared/constants/admins';
import { QUERY_KEYS } from '@avanta/shared/constants/common';
import { UserType } from '@avanta/shared/constants/users';
import { type User } from '@avanta/shared/types/users';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ENTERPRISE_ACTIVITY_SCOPE } from 'constants/enterprise';
import { EnterprisesService } from 'services/enterprises';
import { useEnterpriseId, useUserStore } from 'stores/user';
import { type EnterpriseActivityItem, type EnterpriseActivityScope } from 'types/enterprises';

const DEFAULT_ACTIVITY_LIMIT = 5;

const AVANTA_ADMIN_ROLES = new Set<string>(Object.values(ADMIN_ROLES));

const getActivityScope = (user: User | null): EnterpriseActivityScope => {
  if (user?.type === UserType.ADMIN_ENTERPRISE && AVANTA_ADMIN_ROLES.has(user.role)) {
    return ENTERPRISE_ACTIVITY_SCOPE.ALL;
  }

  return ENTERPRISE_ACTIVITY_SCOPE.MINE;
};

export const useEnterpriseActivity = (
  limit: number = DEFAULT_ACTIVITY_LIMIT
): UseQueryResult<EnterpriseActivityItem[]> => {
  const enterpriseId = useEnterpriseId();
  const user = useUserStore(state => state.user);
  const scope = getActivityScope(user);

  return useQuery({
    queryKey: [QUERY_KEYS.FETCH_ENTERPRISE_ACTIVITY, enterpriseId, limit, scope],
    queryFn: () => EnterprisesService.getActivity(enterpriseId, { limit, scope }),
    select: response => response.data,
    enabled: !!enterpriseId,
  });
};
